import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { inr, todayStr, fmtDate } from "@/lib/format";
import { DateField } from "@/components/DateField";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Scale, ImageIcon, CheckCircle2 } from "lucide-react";

export default function Reconciliation() {
  const [date, setDate] = useState(todayStr());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);
  const [cons, setCons] = useState("");
  const [consInfo, setConsInfo] = useState(null);
  const [photo, setPhoto] = useState(null);

  const load = () => {
    setLoading(true);
    api.get(`/transactions?type=nonconsumer_sale&date=${date}`).then((r) => setRows(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, [date]);

  const openAllocate = (row) => {
    setSel(row);
    setCons(row.allocated_consumer_no || "");
    setConsInfo(null);
  };

  const validate = async () => {
    if (!cons) return;
    const r = await api.get(`/consumers/validate/${cons}`);
    setConsInfo(r.data);
  };

  const save = async () => {
    if (!cons) { toast.error("Enter a consumer number"); return; }
    try {
      await api.put(`/transactions/${sel.id}/allocate`, { allocated_consumer_no: cons });
      toast.success("Allocated to consumer " + cons);
      setSel(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-6 fade-up">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-head text-2xl font-bold flex items-center gap-2"><Scale className="h-6 w-6 text-primary" /> Reconciliation worksheet</h1>
          <p className="text-sm text-muted-foreground">Allocate each non-consumer (grey-area) sale to a consumer number for HP reconciliation.</p>
        </div>
        <DateField value={date} onChange={setDate} className="w-auto" testid="reconciliation-date-input" />
      </div>

      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <Table data-testid="reconciliation-table">
            <TableHeader>
              <TableRow>
                <TableHead>Time</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Cylinder</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Mode</TableHead>
                <TableHead>Proof</TableHead>
                <TableHead>Allocated to</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>}
              {!loading && rows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No non-consumer sales for this date.</TableCell></TableRow>}
              {rows.map((r) => (
                <TableRow key={r.id} className={r.allocated_consumer_no ? "" : "bg-amber-50/60"}>
                  <TableCell className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</TableCell>
                  <TableCell>{r.customer_name || "—"}{r.customer_phone && <div className="text-xs text-muted-foreground">{r.customer_phone}</div>}</TableCell>
                  <TableCell>{r.cylinder_label}</TableCell>
                  <TableCell className="text-right font-medium tabular-nums">{inr(r.amount)}</TableCell>
                  <TableCell className="uppercase text-xs">{r.payment_mode}</TableCell>
                  <TableCell>{r.upi_photo ? <a className="text-primary inline-flex items-center gap-1 text-xs" href={r.upi_photo} target="_blank" rel="noreferrer"><ImageIcon className="h-3.5 w-3.5" />View</a> : <span className="text-xs text-muted-foreground">{r.payment_mode === "upi" ? "Missing" : "—"}</span>}</TableCell>
                  <TableCell>{r.allocated_consumer_no ? <span className="font-mono text-sm inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />{r.allocated_consumer_no}</span> : <Badge variant="outline" className="bg-amber-100 text-amber-950 border-amber-200">Pending</Badge>}</TableCell>
                  <TableCell className="text-right"><Button size="sm" variant={r.allocated_consumer_no ? "outline" : "default"} onClick={() => openAllocate(r)} data-testid={`allocate-button-${r.id}`}>{r.allocated_consumer_no ? "Change" : "Allocate"}</Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent data-testid="reconciliation-allocate-drawer">
          <DialogHeader><DialogTitle>Allocate to consumer number</DialogTitle></DialogHeader>
          {sel && (
            <div className="space-y-4">
              <div className="rounded-lg bg-secondary p-3 text-sm">
                <div>{sel.customer_name || "Non-consumer"} · {sel.cylinder_label}</div>
                <div className="font-semibold">{inr(sel.amount)} · {sel.payment_mode.toUpperCase()}</div>
              </div>
              <div className="flex gap-2">
                <Input placeholder="Consumer number" value={cons} onChange={(e) => setCons(e.target.value.replace(/\D/g, ""))} className="font-mono" data-testid="allocate-consumer-input" />
                <Button variant="outline" onClick={validate}>Check</Button>
              </div>
              {consInfo && (consInfo.valid ? (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
                  <div className="font-medium">{consInfo.consumer.name}</div>
                  <div className="text-xs">{consInfo.consumer.area} · {consInfo.consumer.package_desc}</div>
                </div>
              ) : (
                <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">Consumer number not found in master data.</div>
              ))}
              <Button className="w-full" onClick={save} data-testid="allocate-save-button">Save allocation</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
